import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  InputNumber,
  Space,
  Typography,
  Switch,
  message,
  Skeleton,
  Divider,
} from 'antd';
import { AppstoreOutlined, ReloadOutlined, SaveOutlined, GlobalOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { appSettingsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

const LOCALE_OPTIONS = [
  { value: 'fa', label: 'فارسی (fa)' },
  { value: 'en', label: 'English (en)' },
];

const TIME_FORMAT_OPTIONS = [
  { value: 'h:i a', label: '12h (مثال: 07:35 pm)' },
  { value: 'H:i', label: '24h (مثال: 19:35)' },
];

const AppSettings = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [dateFormats, setDateFormats] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const companyId = useMemo(() => user?.company_id || null, [user?.company_id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appSettingsApi.getTab('general');
      const data = res?.data || {};
      setDateFormats(Array.isArray(data?.date_format) ? data.date_format : []);
      setTimezones(Array.isArray(data?.timezones) ? data.timezones : []);
      setCurrencies(Array.isArray(data?.currencies) ? data.currencies : []);
      form.setFieldsValue({
        currency_id: data?.currencies?.[0]?.id,
        timezone: data?.timezone || 'Asia/Tehran',
        locale: data?.locale || 'fa',
        date_format: data?.date_format?.[0] || 'Y/m/d',
        time_format: data?.time_format || 'h:i a',
        employee_can_export_data: Number(data?.employee_can_export_data) === 1,
        datatable_row_limit: Number(data?.datatable_row_limit) || 10,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت تنظیمات اپلیکیشن');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (values) => {
    if (!companyId) {
      message.error('شناسه شرکت یافت نشد. لطفا مجدد وارد شوید.');
      return;
    }
    setSubmitting(true);
    try {
      await appSettingsApi.update(companyId, {
        currency_id: values.currency_id,
        timezone: values.timezone,
        locale: values.locale,
        date_format: values.date_format,
        time_format: values.time_format,
        employee_can_export_data: values.employee_can_export_data ? 1 : 0,
        datatable_row_limit: Number(values.datatable_row_limit || 10),
      });
      message.success('تنظیمات اپلیکیشن بروزرسانی شد');
      fetchData();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700/70 flex items-center justify-center">
                <AppstoreOutlined className="text-gray-700 dark:text-gray-300 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">تنظیمات اپلیکیشن</h1>
                <Text type="secondary">تنظیمات عمومی نمایش زمان، تاریخ، زبان و خروجی داده</Text>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              بروزرسانی
            </Button>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              {loading ? (
                <Skeleton active paragraph={{ rows: 10 }} />
              ) : (
                <Form form={form} layout="vertical" onFinish={onSubmit} className="custom-font">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Form.Item
                      name="currency_id"
                      label="واحد پول پیش‌فرض"
                      rules={[{ required: true, message: 'واحد پول را انتخاب کنید' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={currencies.map((c) => ({
                          value: c.id,
                          label: `${c.currency_name} (${c.currency_code}) ${c.currency_symbol || ''}`.trim(),
                        }))}
                        placeholder="انتخاب واحد پول"
                        popupClassName="custom-font"
                      />
                    </Form.Item>

                    <Form.Item
                      name="locale"
                      label="زبان پیش‌فرض"
                      rules={[{ required: true, message: 'زبان را انتخاب کنید' }]}
                    >
                      <Select options={LOCALE_OPTIONS} popupClassName="custom-font" />
                    </Form.Item>

                    <Form.Item
                      name="date_format"
                      label="فرمت تاریخ"
                      rules={[{ required: true, message: 'فرمت تاریخ را انتخاب کنید' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={dateFormats.map((d) => ({ value: d, label: d }))}
                        placeholder="انتخاب فرمت تاریخ"
                        popupClassName="custom-font"
                      />
                    </Form.Item>

                    <Form.Item
                      name="time_format"
                      label="فرمت زمان"
                      rules={[{ required: true, message: 'فرمت زمان را انتخاب کنید' }]}
                    >
                      <Select options={TIME_FORMAT_OPTIONS} popupClassName="custom-font" />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="timezone"
                    label="Timezone"
                    rules={[{ required: true, message: 'Timezone را انتخاب کنید' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="مثال: Asia/Tehran"
                      suffixIcon={<GlobalOutlined />}
                      options={timezones.map((tz) => ({ value: tz, label: tz }))}
                      popupClassName="custom-font"
                    />
                  </Form.Item>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Form.Item
                      name="datatable_row_limit"
                      label="تعداد پیش‌فرض ردیف جدول"
                      rules={[{ required: true, message: 'تعداد ردیف را مشخص کنید' }]}
                    >
                      <InputNumber min={5} max={500} className="w-full" />
                    </Form.Item>

                    <Form.Item name="employee_can_export_data" label="امکان خروجی داده برای کارمند" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>

                  <Divider />
                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        icon={<SaveOutlined />}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره تغییرات
                      </Button>
                      <Button onClick={() => form.resetFields()} disabled={submitting}>
                        بازنشانی فرم
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AppSettings;
